"use client";

import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import Card from "../ui/Card";
import Button from "../ui/Button";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import type { Company } from "@/types";

export default function HighlightsWeek({
  title = "Destaques da semana",
  subtitle = "Confira os principais comércios e eventos em destaque",
  companies = [],
}: {
  title?: string;
  subtitle?: string;
  companies?: Company[];
}) {
  const swiperRef = useRef<SwiperType | null>(null);

  // Se não houver pelo menos 10 empresas, não mostrar a seção
  if (!companies || companies.length < 10) {
    return null;
  }

  return (
    <section className="py-12 md:py-24 overflow-hidden">
      <div className="w-full max-w-5xl mx-auto px-4">
        <div className="mb-6 md:mb-12 flex flex-col md:flex-row items-start justify-between gap-4">
          <header className="grid gap-2">
            <h2 className="text-2xl md:text-4xl">{title}</h2>
            <p className="md:text-lg">{subtitle}</p>
          </header>

          <div className="flex gap-4">
            <button
              onClick={() => swiperRef.current?.slidePrev()}
              className="w-14 h-14 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center"
              aria-label="Anterior"
            >
              <ChevronLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
            <button
              onClick={() => swiperRef.current?.slideNext()}
              className="w-14 h-14 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center"
              aria-label="Próximo"
            >
              <ChevronRightIcon className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          spaceBetween={20}
          loop={true}
          centeredSlides={true}
          slidesPerView={1.2}
          className="mb-6 md:mb-12 overflow-visible!"
          breakpoints={{
            640: {
              slidesPerView: 2.5,
            },
            940: {
              slidesPerView: 3.5,
            },
          }}
        >
          {companies.slice(0, 10).map((company) => (
            <SwiperSlide key={company.id}>
              <Card
                name={company.name}
                description={company.description || "Sem descrição disponível"}
                image={company.logo}
                href={`/empresa/${company.slug}`}
                variant="vertical"
                sponsored={company.sponsored}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="flex justify-center">
          <Button href="/empresas?featured=true" variant="primary">
            Ver todos
          </Button>
        </div>
      </div>
    </section>
  );
}
